/**
 * The two things a coach needs mid-session, kept out of the way until then.
 *
 * A module whose body is "here are the games" has two pieces of writing that
 * are not part of that body and should not be read in order with it:
 *
 *   How to regress, which you want at the moment a player is stuck, not
 *   twenty minutes earlier when you were planning.
 *
 *   What order to run them in, which you want once, before the session, and
 *   never again.
 *
 * Putting either inline makes the module longer and buries the drills, which
 * are the module. So both live as buttons in the reader header, beside the
 * Intro button, and open a sheet. Up there they are reachable from any scroll
 * position, they read as belonging to the module rather than to whichever
 * chapter you happen to be in, and the body stays nothing but drills.
 *
 * A sheet may carry drills of its own (the on-air regressions). Those use the
 * same tile as the tabs, so a drill looks the same everywhere and a clip a
 * coach has already seen comes back marked here too.
 *
 * No em dashes.
 */
import React, { useEffect, useState } from "react";
import { X, ChevronRight } from "lucide-react";
import { GOLD } from "../constants/theme";
import { useWatched } from "../hooks/useWatched";
import { DrillTile } from "./SkillTabs";
import { DrillTheater } from "./DrillTheater";

const ff = "'DM Sans', sans-serif";
const norm = (t) => String(t || "").trim().toLowerCase();

/** Paragraphs and **bold**, which is all these two sheets need. */
function Para({ md, C }) {
  const bold = (line) =>
    line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**")
        ? <strong key={i} style={{ color: C.text }}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  return (
    <>
      {String(md || "").split("\n\n").filter(Boolean).map((p, i) => (
        <p key={i} style={{ margin: i ? "11px 0 0" : 0, fontSize: 14, lineHeight: 1.65,
          color: C.textMid }}>
          {bold(p)}
        </p>
      ))}
    </>
  );
}

/** A numbered run of steps, for "how to order this". */
function Steps({ steps = [], C }) {
  return (
    <ol style={{ margin: "14px 0 0", padding: 0, listStyle: "none",
      display: "flex", flexDirection: "column", gap: 14 }}>
      {steps.map((s, i) => (
        <li key={i} style={{ display: "flex", gap: 11 }}>
          <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%",
            background: `${GOLD}1f`, border: `1px solid ${GOLD}55`, color: GOLD,
            fontSize: 11.5, fontWeight: 800, display: "flex", alignItems: "center",
            justifyContent: "center", fontVariantNumeric: "tabular-nums" }}>
            {i + 1}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 3 }}>
              {s.title}
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.6, color: C.textMid }}>{s.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function PillSheetModal({ item, C, dark, onClose, fetchDrills, drillCache }) {
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(null);
  const { watched } = useWatched();
  const titles = item.titles || [];

  useEffect(() => {
    let cancelled = false;
    if (!titles.length) { setReady(true); return; }
    (async () => {
      try { await fetchDrills(titles); } catch { /* the sheet just shows its text */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const drills = ready
    ? titles.map(t => drillCache.get(norm(t))).filter(Boolean)
    : [];

  return (
    <div onClick={onClose} role="dialog" aria-modal="true" aria-label={item.title} className="bam-scrim-in"
      style={{ position: "fixed", inset: 0, zIndex: 5800, background: "rgba(0,0,0,0.62)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} className="bam-dialog-in"
        style={{ width: "100%", maxWidth: 560, maxHeight: "82vh", overflowY: "auto",
          borderRadius: 16, padding: "20px 22px 24px", fontFamily: ff,
          background: dark ? "#121212" : "#FFFFFF",
          border: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.4,
              textTransform: "uppercase", color: GOLD, marginBottom: 5 }}>
              {item.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.text, lineHeight: 1.25 }}>
              {item.title}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: "transparent", border: "none", color: C.textDim,
              cursor: "pointer", padding: 4, display: "flex", flexShrink: 0 }}>
            <X size={17}/>
          </button>
        </div>

        {item.md && <Para md={item.md} C={C}/>}
        {item.steps && <Steps steps={item.steps} C={C}/>}

        {titles.length > 0 && (
          <div style={{ marginTop: 16 }}>
            {!ready ? (
              <div style={{ fontSize: 13, color: C.textDim }}>Loading drills...</div>
            ) : drills.length === 0 ? (
              <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.6 }}>
                These drills are not published yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12,
                gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
                {drills.map(d => (
                  <DrillTile key={d.id} d={d} intensity={item.intensity?.[norm(d.title)] || 1}
                    watched={watched.has(d.id)}
                    onPlay={() => setPlaying(d)} C={C}/>
                ))}
              </div>
            )}
          </div>
        )}

        <DrillTheater drill={playing} C={C} dark={dark} onClose={() => setPlaying(null)}/>

        {item.footnote && (
          <div style={{ marginTop: 16, paddingTop: 13, borderTop: `1px solid ${C.border}`,
            fontSize: 12.5, lineHeight: 1.6, color: C.textDim }}>
            {item.footnote}
          </div>
        )}
      </div>
    </div>
  );
}

export default PillSheetModal;
